import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { Typography, Button, message, Modal, Spin } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import type { CommissionRule, LaneCard, ProductTypeId } from './types';
import { FieldLibrary } from './components/FieldLibrary';
import { ParameterSidebar } from './components/ParameterSidebar';
import { RuleBlock } from './components/RuleBlock';
import { saveCommissionGroup } from './actions';
import { getCommissionGroupById } from 'app/shared/api/commissionGroup/getCommissionGroupById';
import { commissionGroupDtoToRule } from 'app/shared/api/commissionGroup/mappers';
import { getAllFees } from 'app/shared/api/getFees';
import { getPackageProducts } from 'app/shared/api/getPackages';
import { getCourseProducts } from 'app/shared/api/getCourseProducts';
import { getSelectedDivisionId } from 'app/shared/division/selectedDivisionStorage';
import { getAccommodationProducts } from 'app/shared/api/getAccommodationProducts';
import { getAddonsProducts } from 'app/shared/api/getAddOnsProducts';
import { getTransferProducts } from 'app/shared/api/getTransferProducts';
import { getAllAccommodationTypes } from 'app/shared/api/getAllAccommodationTypes';
import { getAllAddonTypes } from 'app/shared/api/getAllAddonTypes';
import type { Fee } from 'app/shared/model';
import type { ProductTypeCondition, AccommodationCondition } from './types';
import { Link } from 'react-router-dom';
import { syncMirroredLaneCards, type MirrorSyncContext } from './mirrorSync';

import './AddCommissionPage.css';

const { Title } = Typography;

const generateId = () => `card-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

function getSummary(condition: ProductTypeCondition | AccommodationCondition | undefined): string {
  if (!condition) return 'Specify product';
  if (condition.mode === 'ANY') return 'Any';
  if ('selectedNames' in condition && condition.selectedNames?.length) {
    return condition.selectedNames.join(', ');
  }
  if ('typeName' in condition && condition.typeName) return condition.typeName;
  return 'Specify product';
}

function mapIdToName(rows: { id?: number | string; name?: string }[]): Map<number, string> {
  const m = new Map<number, string>();
  for (const row of rows) {
    const id = Number(row.id);
    if (Number.isFinite(id) && id > 0) m.set(id, String(row.name ?? ''));
  }
  return m;
}

function buildMirrorSyncContext(
  courses: { id: number; name: string }[],
  accommodations: { id: number; name: string }[],
  addons: { id: number; name: string }[],
  transfers: { id: number; name: string }[],
  feesList: Fee[],
  packagesList: { id: number; name: string }[],
  accommodationTypeByProductId: Map<number, number>,
  addOnTypeByProductId: Map<number, number>
): MirrorSyncContext {
  return {
    getOptions: (pt) => {
      switch (pt) {
        case 'Course':
          return courses;
        case 'Accommodation':
          return accommodations;
        case 'AddOn':
          return addons;
        case 'Transfer':
          return transfers;
        case 'Fees':
          return feesList.map((f) => ({ id: f.id, name: f.name }));
        case 'Package':
          return packagesList;
        default:
          return [];
      }
    },
    accommodationTypeByProductId,
    addOnTypeByProductId,
  };
}

export const AddCommissionPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const pathname = location.pathname;
  const isAdd = pathname.endsWith('/add');
  const idMatch = pathname.match(/\/commission-category-manager\/(\d+)$/);
  const editId = idMatch ? Number(idMatch[1]) : null;

  const [rules, setRules] = useState<CommissionRule[]>([]);
  const [fees, setFees] = useState<Fee[]>([]);
  const [courses, setCourses] = useState<{ id: number; name: string }[]>([]);
  const [accommodations, setAccommodations] = useState<{ id: number; name: string }[]>([]);
  const [addons, setAddons] = useState<{ id: number; name: string }[]>([]);
  const [transfers, setTransfers] = useState<{ id: number; name: string }[]>([]);
  const [packages, setPackages] = useState<{ id: number; name: string }[]>([]);
  const [accommodationTypes, setAccommodationTypes] = useState<{ id: number; name: string }[]>([]);
  const [addOnTypes, setAddOnTypes] = useState<{ id: number; name: string }[]>([]);
  const [accommodationTypeByProductId, setAccommodationTypeByProductId] = useState<Map<number, number>>(() => new Map());
  const [addOnTypeByProductId, setAddOnTypeByProductId] = useState<Map<number, number>>(() => new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sidebarCard, setSidebarCard] = useState<{ rule: CommissionRule; lane: 'include' | 'exclude'; card: LaneCard } | null>(null);

  const divisionId = getSelectedDivisionId();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [feesData, coursesData, accData, addonsData, transfersData, packagesData, accTypesData, addonTypesData, groupDto] =
          await Promise.all([
            getAllFees(),
            divisionId ? getCourseProducts(divisionId) : Promise.resolve([]),
            getAccommodationProducts(),
            divisionId ? getAddonsProducts(divisionId) : Promise.resolve([]),
            divisionId ? getTransferProducts(divisionId) : Promise.resolve([]),
            divisionId ? getPackageProducts(divisionId) : Promise.resolve([]),
            getAllAccommodationTypes(),
            getAllAddonTypes(),
            editId && divisionId ? getCommissionGroupById(divisionId, editId) : Promise.resolve(null),
          ]);

        const activeFees = feesData
          .filter((f) => f.active)
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        setFees(activeFees);
        const coursesOpts = (coursesData as { id?: number; name?: string }[])
          .map((c) => ({ id: c.id ?? 0, name: c.name ?? '' }))
          .filter((c) => c.id);
        const accOpts = (accData as { id?: number; name?: string }[])
          .map((a) => ({ id: a.id ?? 0, name: a.name ?? '' }))
          .filter((a) => a.id);
        const addonOpts = (addonsData as { id?: number; name?: string }[])
          .map((a) => ({ id: a.id ?? 0, name: a.name ?? '' }))
          .filter((a) => a.id);
        const accommodationTypeByProductId = new Map(
          (accData as { id?: number; typeId?: number }[])
            .filter((a) => (a.id ?? 0) > 0 && a.typeId != null && Number(a.typeId) > 0)
            .map((a) => [a.id as number, Number(a.typeId)])
        );
        const addOnTypeByProductId = new Map(
          (addonsData as { id?: number; typeId?: number }[])
            .filter((a) => (a.id ?? 0) > 0 && a.typeId != null && Number(a.typeId) > 0)
            .map((a) => [a.id as number, Number(a.typeId)])
        );
        const packageOpts = (packagesData as { id?: number; name?: string }[])
          .map((p) => ({ id: p.id ?? 0, name: p.name ?? '' }))
          .filter((p) => p.id > 0);
        setCourses(coursesOpts);
        setAccommodations(accOpts);
        setAddons(addonOpts);
        setAccommodationTypeByProductId(accommodationTypeByProductId);
        setAddOnTypeByProductId(addOnTypeByProductId);
        setTransfers((transfersData as { id?: number; name?: string }[]).map((t) => ({ id: t.id ?? 0, name: t.name ?? '' })).filter((t) => t.id));
        setPackages(packageOpts);
        setAccommodationTypes(
          (accTypesData as { id?: number; name?: string }[])
            .map((t) => ({ id: Number(t.id) || 0, name: String(t.name ?? '') }))
            .filter((t) => t.id > 0)
        );
        setAddOnTypes(
          (addonTypesData as { id?: number; name?: string }[])
            .map((t) => ({ id: Number(t.id) || 0, name: String(t.name ?? '') }))
            .filter((t) => t.id > 0)
        );

        if (isAdd) {
          setRules([
            {
              id: `rule-${Date.now()}`,
              name: '',
              include: [],
              exclude: [],
              feesIncludeAny: true,
              fees: [],
              active: true,
            },
          ]);
        } else if (groupDto) {
          const feeNames = new Map(activeFees.map((f) => [f.id, f.name]));
          const coursesMap = new Map((coursesData as { id?: number; name?: string }[]).map((c) => [c.id ?? 0, c.name ?? '']));
          const accMap = new Map((accData as { id?: number; name?: string }[]).map((a) => [a.id ?? 0, a.name ?? '']));
          const addonsMap = new Map((addonsData as { id?: number; name?: string }[]).map((a) => [a.id ?? 0, a.name ?? '']));
          const transfersMap = new Map((transfersData as { id?: number; name?: string }[]).map((t) => [t.id ?? 0, t.name ?? '']));
          const packagesMap = new Map(packageOpts.map((p) => [p.id, p.name]));
          const accTypesMap = mapIdToName(accTypesData as { id?: number; name?: string }[]);
          const addOnTypesMap = mapIdToName(addonTypesData as { id?: number; name?: string }[]);
          const feesMap = new Map(activeFees.map((f) => [f.id, f.name]));
          const productNames = {
            courses: coursesMap,
            accommodations: accMap,
            addons: addonsMap,
            transfers: transfersMap,
            accommodationTypes: accTypesMap,
            addOnTypes: addOnTypesMap,
            fees: feesMap,
            packages: packagesMap,
          };
          const loadedRule = commissionGroupDtoToRule(groupDto, feeNames, productNames);
          const transferOpts = (transfersData as { id?: number; name?: string }[])
            .map((t) => ({ id: t.id ?? 0, name: t.name ?? '' }))
            .filter((t) => t.id);
          const mirrorCtx = buildMirrorSyncContext(
            coursesOpts,
            accOpts,
            addonOpts,
            transferOpts,
            activeFees,
            packageOpts,
            accommodationTypeByProductId,
            addOnTypeByProductId
          );
          setRules([syncMirroredLaneCards(loadedRule, mirrorCtx)]);
        } else if (editId) {
          message.error('Commission not found. It may have been deleted.');
          navigate('/commission-category-manager');
        }
      } catch (err) {
        message.error('Failed to load commission. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [divisionId, isAdd, editId, navigate]);

  const getOptionsForProductType = useCallback(
    (pt: ProductTypeId) => {
      switch (pt) {
        case 'Course':
          return courses;
        case 'Accommodation':
          return accommodations;
        case 'AddOn':
          return addons;
        case 'Transfer':
          return transfers;
        case 'Fees':
          return fees.map((f) => ({ id: f.id, name: f.name }));
        case 'Package':
          return packages;
        default:
          return [];
      }
    },
    [courses, accommodations, addons, transfers, fees, packages]
  );

  const mirrorContext = useMemo<MirrorSyncContext>(
    () => ({
      getOptions: (pt) => getOptionsForProductType(pt),
      accommodationTypeByProductId,
      addOnTypeByProductId,
    }),
    [getOptionsForProductType, accommodationTypeByProductId, addOnTypeByProductId]
  );

  const disabledFieldLibraryProductTypes = useMemo(() => {
    const s = new Set<ProductTypeId>();
    rules.forEach((r) => {
      [...r.include, ...r.exclude].forEach((c) => s.add(c.productType));
    });
    return s;
  }, [rules]);

  const updateRule = useCallback((index: number, rule: CommissionRule) => {
    setRules((prev) => {
      const next = [...prev];
      next[index] = rule;
      return next;
    });
  }, []);

  const removeCard = useCallback(
    (ruleIndex: number, lane: 'include' | 'exclude', cardId: string) => {
    Modal.confirm({
      title: 'Delete card',
      content: 'Are you sure you want to delete?',
        okText: 'Yes',
        cancelText: 'Cancel',
        onOk: () => {
          setRules((prev) => {
            const r = prev[ruleIndex];
            if (!r) return prev;
            const next = [...prev];
            const filtered =
              lane === 'include'
                ? r.include.filter((c) => c.id !== cardId)
                : r.exclude.filter((c) => c.id !== cardId);
            const updated =
              lane === 'include' ? { ...r, include: filtered } : { ...r, exclude: filtered };
            next[ruleIndex] = syncMirroredLaneCards(updated, mirrorContext);
            return next;
          });
        },
      });
    },
    [mirrorContext]
  );

  const updateCardCondition = useCallback(
    (ruleIndex: number, lane: 'include' | 'exclude', cardId: string, condition: ProductTypeCondition | AccommodationCondition) => {
      setRules((prev) => {
        const r = prev[ruleIndex];
        if (!r) return prev;
        const cards = lane === 'include' ? r.include : r.exclude;
        const card = cards.find((c) => c.id === cardId);
        if (!card) return prev;
        const summary = getSummary(condition);
        const updatedCard = { ...card, condition, summary };
        const next = [...prev];
        const updatedRule =
          lane === 'include'
            ? { ...r, include: cards.map((c) => (c.id === cardId ? updatedCard : c)) }
            : { ...r, exclude: cards.map((c) => (c.id === cardId ? updatedCard : c)) };
        next[ruleIndex] = syncMirroredLaneCards(updatedRule, mirrorContext);
        return next;
      });
      setSidebarCard(null);
    },
    [mirrorContext]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;

      const source = active.data.current;
      const target = over.data.current;
      if (!source?.productType || source.source !== 'field-library' || !target?.lane || !target?.ruleId) return;

      const productType = source.productType as ProductTypeId;
      const lane = target.lane as 'include' | 'exclude';
      const ruleIndex = rules.findIndex((r) => r.id === target.ruleId);
      if (ruleIndex < 0) return;

      const rule = rules[ruleIndex];
      const otherLane = lane === 'include' ? rule.exclude : rule.include;
      const sameLane = lane === 'include' ? rule.include : rule.exclude;

      if (sameLane.some((c) => c.productType === productType)) {
        message.warning(`You already added "${productType}" to ${lane === 'include' ? 'Include' : 'Exclude'} for this rule.`);
        return;
      }
      if (otherLane.some((c) => c.productType === productType)) {
        message.warning(`You can't add "${productType}" to both Include and Exclude for the same rule`);
        return;
      }

      const newCard: LaneCard = {
        id: generateId(),
        productType,
        summary: 'Specify product',
      };

      const ruleWithCard =
        lane === 'include'
          ? { ...rule, include: [...rule.include, newCard] }
          : { ...rule, exclude: [...rule.exclude, newCard] };

      setRules((prev) => {
        const next = [...prev];
        next[ruleIndex] = ruleWithCard;
        return next;
      });

      setSidebarCard({ rule: ruleWithCard, lane, card: newCard });
    },
    [rules]
  );

  const hasIncompleteProductSelection = useCallback((rule: CommissionRule) => {
    const checkCard = (card: LaneCard) => {
      const c = card.condition;
      if (!c) return false;
      if (c.mode === 'SELECT' && (!('selectedIds' in c) || !c.selectedIds?.length)) return true;
      if (c.mode === 'CHOOSE_TYPE' && (!('typeId' in c) || c.typeId == null || Number(c.typeId) <= 0)) return true;
      return false;
    };
    return rule.include.some(checkCard) || rule.exclude.some(checkCard);
  }, []);

  const handleSave = useCallback(async () => {
    if (rules.length === 0) {
      message.error('Please add at least one rule.');
      return;
    }
    const firstRule = rules[0];
    const name = firstRule.name?.trim();
    if (!name) {
      message.error('Please enter a rule name.');
      return;
    }
    const ruleWithIncomplete = rules.find(hasIncompleteProductSelection);
    if (ruleWithIncomplete) {
      message.error('Product selections with "Select" or "Choose Type" must have at least one item selected. Please configure each product card.');
      return;
    }
    setSaving(true);
    try {
      const res = await dispatch(
        saveCommissionGroup(rules, editId) as any
      );
      if (res?.ok) {
        message.success(editId ? 'Commission updated successfully.' : 'Commission created successfully.');
        navigate('/commission-category-manager');
      } else {
        message.error(res?.message || 'Failed to save commission.');
      }
    } catch (err: any) {
      message.error(err?.message || 'Failed to save commission.');
    } finally {
      setSaving(false);
    }
  }, [rules, editId, navigate, dispatch, hasIncompleteProductSelection]);

  const hasErrors =
    rules.length === 0 ||
    rules.some((r) => !r.name?.trim()) ||
    rules.some(hasIncompleteProductSelection);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  if (loading) {
    return (
      <div className="commission-loading">
        <Spin size="large" />
        <span className="commission-loading-text">Loading...</span>
      </div>
    );
  }

  return (
    <div className="commission-builder-page">
        <div className="commission-back-link-wrap">
          <Link to="/commission-category-manager" className="commission-back-link">
            <LeftOutlined />
            Back to Commissions
          </Link>
        </div>
        <Title level={4}>{isAdd ? 'Add New Commission' : 'Edit Commission'}</Title>

        <div className="commission-builder-layout">
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <div className="commission-builder-main">
              {rules.map((rule, idx) => (
                <RuleBlock
                  key={rule.id}
                  rule={rule}
                  onRuleChange={(r) => updateRule(idx, r)}
                  onCardRemove={(lane, cardId) => removeCard(idx, lane, cardId)}
                  onCardConfigure={(lane, card) => setSidebarCard({ rule, lane, card })}
                />
              ))}
            </div>
            {!sidebarCard && (
              <div className="commission-builder-sidebar">
                <FieldLibrary disabledProductTypes={disabledFieldLibraryProductTypes} />
              </div>
            )}
          </DndContext>
          {sidebarCard && !sidebarCard.card.isMirror && (
            <div className="commission-builder-sidebar">
              <ParameterSidebar
                key={sidebarCard.card.id}
                productType={sidebarCard.card.productType}
                card={sidebarCard.card}
                options={getOptionsForProductType(sidebarCard.card.productType)}
                accommodationTypes={sidebarCard.card.productType === 'Accommodation' ? accommodationTypes : undefined}
                addOnTypes={sidebarCard.card.productType === 'AddOn' ? addOnTypes : undefined}
                onSave={(cond) => {
                  const ri = rules.findIndex((r) => r.id === sidebarCard.rule.id);
                  if (ri >= 0) updateCardCondition(ri, sidebarCard.lane, sidebarCard.card.id, cond);
                }}
                onClear={() => {
                  const ri = rules.findIndex((r) => r.id === sidebarCard.rule.id);
                  if (ri >= 0) updateCardCondition(ri, sidebarCard.lane, sidebarCard.card.id, { mode: 'ANY', selectedIds: [] });
                }}
                onClose={() => setSidebarCard(null)}
              />
            </div>
          )}
        </div>

        <div className="commission-save-row">
          <Button type="primary" onClick={handleSave} disabled={hasErrors} loading={saving}>
            Save
          </Button>
        </div>
      </div>
  );
};